import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, StampIcon, FileText, CreditCard, Upload,
  Clock, CalendarCheck, LogIn, ShieldCheck, Loader2, MessageCircle, Mail, CheckCircle2,
} from 'lucide-react';
import { getVisa, flagUrl, VISA_GST, VISA_HERO, priceText } from '../visaData';
import { company } from '../mock';
import { createOrder, verifyPayment, loadRazorpay, createEnquiry } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useEnquiry } from '../context/EnquiryContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const STEPS = [
  { n: 1, label: 'Basic Details', icon: FileText },
  { n: 2, label: 'Traveller Details', icon: StampIcon },
  { n: 3, label: 'Review & Payment', icon: CreditCard },
  { n: 4, label: 'Upload Documents', icon: Upload },
];

const inputCls = 'w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] text-[#0B2545] placeholder-slate-400 outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/15 transition bg-white';
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function VisaDetail() {
  const { slug } = useParams();
  const visa = getVisa(slug);
  const { toast } = useToast();
  const { openEnquiry } = useEnquiry();

  const [step, setStep] = useState(1);
  const [dateOfTravel, setDateOfTravel] = useState('');
  const [counts, setCounts] = useState({ adult: 1, child: 0, infant: 0 });
  const [travellers, setTravellers] = useState([]);
  const [paying, setPaying] = useState(false);
  const [paidInfo, setPaidInfo] = useState(null);

  const totalPax = counts.adult + counts.child + counts.infant;
  const cost = useMemo(() => (visa ? visa.fee * totalPax : 0), [visa, totalPax]);
  const tax = useMemo(() => Math.round(cost * VISA_GST), [cost]);
  const total = cost + tax;

  if (!visa) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-5 pt-40 pb-24 text-center">
          <h1 className="font-heading text-3xl font-extrabold text-[#0B2545]">Visa not found</h1>
          <Link to="/visas" className="mt-4 inline-block text-[#EA5019] font-semibold">← Back to all visas</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const info = [
    ['Processing time', visa.processingTime],
    ['Visa Type', visa.visaType],
    ['Stay period', visa.stayPeriod],
    ['Validity', visa.validity],
    ['Entry', visa.entry],
    ['Visa Mode', visa.visaMode],
  ].filter(([, v]) => v);

  const buildTravellers = () => {
    const list = [];
    const add = (type, n) => { for (let i = 0; i < n; i++) list.push({ type, first_name: '', last_name: '', dob: '', passport_no: '', nationality: 'India' }); };
    add('Adult', counts.adult);
    add('Child', counts.child);
    add('Infant', counts.infant);
    return list;
  };

  const enquiryPayload = (extra = {}) => ({
    name: travellers[0] ? `${travellers[0].first_name} ${travellers[0].last_name}`.trim() : `${visa.country} Visa Applicant`,
    email: extra.email || '',
    phone: extra.phone || '',
    destination: visa.country,
    category: 'Visa',
    travel_date: dateOfTravel,
    pax: String(totalPax),
    source: 'visa-booking',
    remarks: `${visa.visaType} — Adults: ${counts.adult}, Children: ${counts.child}, Infants: ${counts.infant}. Total: ${fmt(total)} (incl. 18% GST).`,
    ...extra,
  });

  const onGetQuote = () => {
    openEnquiry('Visa', {
      destination: visa.country,
      travel_date: dateOfTravel,
      nationality: 'India',
      pax: String(totalPax),
      remarks: `Visa quote request — ${visa.country} ${visa.visaType}. Adults: ${counts.adult}, Children: ${counts.child}, Infants: ${counts.infant}. Estimated ${fmt(total)} incl. 18% GST. Please share your best quote.`,
    });
  };

  const goToTravellers = () => {
    if (!dateOfTravel) { toast({ title: 'Select your date of travel' }); return; }
    if (counts.adult < 1) { toast({ title: 'At least 1 adult is required' }); return; }
    if (travellers.length !== totalPax) setTravellers(buildTravellers());
    setStep(2);
  };

  const setTraveller = (i, field, value) => {
    setTravellers((ts) => ts.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  };

  const validateTravellers = () => {
    for (const t of travellers) {
      if (!t.first_name.trim() || !t.last_name.trim() || !t.dob || !t.passport_no.trim()) return false;
    }
    return true;
  };

  const goToReview = () => {
    if (!validateTravellers()) { toast({ title: 'Please complete all traveller details', description: 'First name, last name, date of birth and passport number are required.' }); return; }
    setStep(3);
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast({ title: 'Unable to load payment gateway' }); setPaying(false); return; }
      const order = await createOrder({
        amount: total,
        package: `${visa.country} ${visa.visaType} x ${totalPax} (incl. 18% GST)`,
        name: travellers[0] ? `${travellers[0].first_name} ${travellers[0].last_name}`.trim() : `${visa.country} Visa`,
      });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: company.name,
        description: `${visa.country} ${visa.visaType} — ${totalPax} traveller(s)`,
        image: company.logo,
        theme: { color: '#EA5019' },
        handler: async (res) => {
          try {
            await verifyPayment(res);
            createEnquiry(enquiryPayload({ source: 'visa-paid', status: 'new', remarks: `PAID ${fmt(total)} — ${visa.country} ${visa.visaType}. Payment ID: ${res.razorpay_payment_id}. Adults: ${counts.adult}, Children: ${counts.child}, Infants: ${counts.infant}. Travel date: ${dateOfTravel}.` })).catch(() => {});
            setPaidInfo({ paymentId: res.razorpay_payment_id, amount: total });
            setStep(4);
            toast({ title: 'Payment successful!', description: 'Your visa application has been initiated.' });
          } catch (e) {
            toast({ title: 'Payment could not be verified', description: 'If money was deducted, contact us with your payment ID.' });
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on('payment.failed', () => { toast({ title: 'Payment failed', description: 'Please try again.' }); setPaying(false); });
      rzp.open();
      setPaying(false);
    } catch (e) {
      toast({ title: 'Could not start payment' });
      setPaying(false);
    }
  };

  const waDocs = `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(`Hi *${company.name}*!\nI have paid for my *${visa.country} ${visa.visaType}* (Payment ID: ${paidInfo?.paymentId || ''}). I'd like to submit my documents.`)}`;

  return (
    <div className="bg-[#f6f8fb]">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0B2545] overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url('${visa.img || VISA_HERO}')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/85 to-[#0B2545]/40" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8">
          <Link to="/visas" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> All visas</Link>
          <div className="flex items-center gap-4">
            <img src={flagUrl(visa.countryCode)} alt={`${visa.country} flag`} className="h-11 w-16 object-cover rounded shadow-lg" />
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white">{visa.country}</h1>
              <p className="text-[#ff9a6a] font-semibold mt-1">{visa.visaType}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 -mt-8 relative">
        {/* Stepper */}
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(11,37,69,0.08)] p-4 md:p-5 mb-6" data-testid="visa-stepper">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((s, idx) => {
              const active = step === s.n;
              const done = step > s.n;
              return (
                <React.Fragment key={s.n}>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className={`grid place-items-center h-8 w-8 rounded-md text-sm font-bold ${active ? 'bg-[#EA5019] text-white' : done ? 'bg-[#22a06b] text-white' : 'bg-slate-100 text-slate-400'}`}>
                      {done ? <Check size={16} /> : s.n}
                    </span>
                    <span className={`text-sm font-semibold whitespace-nowrap ${active || done ? 'text-[#0B2545]' : 'text-slate-400'}`}>{s.label}</span>
                  </div>
                  {idx < STEPS.length - 1 && <span className={`flex-1 h-px mx-2 min-w-4 ${done ? 'bg-[#22a06b]' : 'bg-slate-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Left column: step content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visa info card (always visible on step 1) */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-info-card">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {info.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="font-semibold text-[#0B2545]">{k} :</span>
                      <span className="text-slate-600">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 1: Basic Details */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-step-basic">
                <h2 className="font-heading text-xl font-bold text-[#0B2545] mb-5">Basic Details</h2>
                <div className="mb-5 max-w-xs">
                  <label className="text-sm font-medium text-[#0B2545]">Date Of Travel *</label>
                  <input type="date" data-testid="visa-date-of-travel" value={dateOfTravel} onChange={(e) => setDateOfTravel(e.target.value)} className={`${inputCls} mt-1.5`} />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[['adult', 'Adult *', 0], ['child', 'Child', 0], ['infant', 'Infant', 0]].map(([key, label]) => (
                    <div key={key}>
                      <label className="text-sm font-medium text-[#0B2545]">{label}</label>
                      <input
                        type="number"
                        min={key === 'adult' ? 1 : 0}
                        data-testid={`visa-count-${key}`}
                        value={counts[key]}
                        onChange={(e) => setCounts({ ...counts, [key]: Math.max(key === 'adult' ? 1 : 0, Number(e.target.value) || 0) })}
                        placeholder={`No of ${label.replace(' *', '')}`}
                        className={`${inputCls} mt-1.5`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex flex-wrap gap-3">
                  <button onClick={onGetQuote} data-testid="visa-get-quote-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#EA5019] text-[#EA5019] font-semibold hover:bg-[#EA5019]/5 transition">
                    Get Quote
                  </button>
                  {!visa.quoteOnly && (
                    <button onClick={goToTravellers} data-testid="visa-book-btn" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-[#EA5019] text-white font-semibold hover:bg-[#cf4413] transition shadow-[0_10px_26px_rgba(234,80,25,0.3)]">
                      Book <ArrowRight size={17} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2: Traveller Details */}
            {step === 2 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-step-travellers">
                <h2 className="font-heading text-xl font-bold text-[#0B2545] mb-1">Traveller Details</h2>
                <p className="text-sm text-slate-500 mb-5">Enter details exactly as per passport.</p>
                <div className="space-y-6">
                  {travellers.map((t, i) => (
                    <div key={i} className="rounded-xl border border-slate-100 p-4">
                      <p className="font-semibold text-[#0B2545] mb-3 flex items-center gap-2"><span className="text-xs px-2.5 py-1 rounded-full bg-[#0B2545]/10">{t.type} {i + 1}</span></p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input data-testid={`visa-traveller-${i}-first`} placeholder="First Name *" value={t.first_name} onChange={(e) => setTraveller(i, 'first_name', e.target.value)} className={inputCls} />
                        <input data-testid={`visa-traveller-${i}-last`} placeholder="Last Name *" value={t.last_name} onChange={(e) => setTraveller(i, 'last_name', e.target.value)} className={inputCls} />
                        <div>
                          <label className="text-xs text-slate-400">Date of Birth *</label>
                          <input type="date" data-testid={`visa-traveller-${i}-dob`} value={t.dob} onChange={(e) => setTraveller(i, 'dob', e.target.value)} className={inputCls} />
                        </div>
                        <input data-testid={`visa-traveller-${i}-passport`} placeholder="Passport Number *" value={t.passport_no} onChange={(e) => setTraveller(i, 'passport_no', e.target.value)} className={inputCls} />
                        <input data-testid={`visa-traveller-${i}-nationality`} placeholder="Nationality" value={t.nationality} onChange={(e) => setTraveller(i, 'nationality', e.target.value)} className={`${inputCls} sm:col-span-2`} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-between">
                  <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-[#0B2545] font-semibold hover:bg-slate-50 transition"><ArrowLeft size={16} /> Back</button>
                  <button onClick={goToReview} data-testid="visa-to-review-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#EA5019] text-white font-semibold hover:bg-[#cf4413] transition">Continue <ArrowRight size={17} /></button>
                </div>
              </div>
            )}

            {/* STEP 3: Review & Payment */}
            {step === 3 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-step-review">
                <h2 className="font-heading text-xl font-bold text-[#0B2545] mb-5">Review & Payment</h2>
                <div className="rounded-xl bg-[#f6f8fb] p-4 mb-5 text-sm">
                  <div className="flex justify-between py-1"><span className="text-slate-500">Visa</span><span className="font-semibold text-[#0B2545]">{visa.country} — {visa.visaType}</span></div>
                  <div className="flex justify-between py-1"><span className="text-slate-500">Date of Travel</span><span className="font-semibold text-[#0B2545]">{dateOfTravel}</span></div>
                  <div className="flex justify-between py-1"><span className="text-slate-500">Travellers</span><span className="font-semibold text-[#0B2545]">{counts.adult} Adult, {counts.child} Child, {counts.infant} Infant</span></div>
                </div>
                <div className="space-y-2 mb-4">
                  {travellers.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <span className="text-[#0B2545] font-medium">{t.first_name} {t.last_name} <span className="text-slate-400">({t.type})</span></span>
                      <span className="text-slate-500">Passport: {t.passport_no}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 text-sm text-slate-500 bg-[#fff7f3] border border-[#EA5019]/20 rounded-xl p-3 mb-5">
                  <ShieldCheck size={18} className="text-[#EA5019] shrink-0 mt-0.5" /> Secure payment via Razorpay. After payment, you'll be guided to submit your documents.
                </div>
                <div className="flex justify-between">
                  <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-[#0B2545] font-semibold hover:bg-slate-50 transition"><ArrowLeft size={16} /> Back</button>
                  <button onClick={payNow} disabled={paying} data-testid="visa-pay-btn" className={`inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#EA5019] text-white font-semibold hover:bg-[#cf4413] transition shadow-[0_10px_26px_rgba(234,80,25,0.3)] ${paying ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    {paying ? <><Loader2 size={18} className="animate-spin" /> Processing…</> : <><CreditCard size={18} /> Pay {fmt(total)} & Submit</>}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Documents */}
            {step === 4 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-step-documents">
                <div className="text-center mb-6">
                  <span className="grid place-items-center h-16 w-16 mx-auto rounded-full bg-[#22a06b]/10 text-[#22a06b]"><CheckCircle2 size={34} /></span>
                  <h2 className="mt-4 font-heading text-2xl font-extrabold text-[#0B2545]">Payment Received!</h2>
                  <p className="mt-2 text-slate-500">Your {visa.country} {visa.visaType} application has been initiated{paidInfo?.paymentId ? ` (Payment ID: ${paidInfo.paymentId})` : ''}. Please submit the documents below to complete your application.</p>
                </div>
                <h3 className="font-heading font-bold text-[#0B2545] mb-3">Documents Required</h3>
                <div className="space-y-3">
                  {visa.documents.map((d) => (
                    <div key={d.title} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                      <FileText size={18} className="text-[#EA5019] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#0B2545]">{d.title}{d.required && <span className="text-[#EA5019]"> *</span>}</p>
                        <p className="text-sm text-slate-500 mt-0.5">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={waDocs} target="_blank" rel="noreferrer" data-testid="visa-docs-whatsapp" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-semibold hover:brightness-95 transition"><MessageCircle size={18} /> Send Documents on WhatsApp</a>
                  <a href={`mailto:${company.email}?subject=${encodeURIComponent(`${visa.country} Visa Documents`)}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-[#0B2545] font-semibold hover:bg-slate-50 transition"><Mail size={18} /> Email Documents</a>
                </div>
                <Link to="/visas" className="mt-6 inline-block text-[#EA5019] font-semibold">← Apply for another visa</Link>
              </div>
            )}

            {/* Document requirements list (visible on step 1) */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.06)]" data-testid="visa-documents-list">
                <h3 className="font-heading text-xl font-bold text-[#0B2545] mb-4">Documents Required</h3>
                <div className="space-y-3">
                  {visa.documents.map((d) => (
                    <div key={d.title} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                      <FileText size={18} className="text-[#EA5019] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#0B2545]">{d.title}{d.required && <span className="text-[#EA5019]"> *</span>}</p>
                        <p className="text-sm text-slate-500 mt-0.5">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column: price summary */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(11,37,69,0.08)]" data-testid="visa-price-summary">
              <p className="font-heading text-lg font-extrabold text-[#EA5019] mb-4">{visa.visaType}</p>
              {visa.quoteOnly ? (
                <div className="text-center py-2">
                  <span className="text-xs text-slate-400">Approx. embassy fee</span>
                  <p className="font-heading text-2xl font-extrabold text-[#0B2545] mt-1">{priceText(visa)}</p>
                  <p className="text-sm text-slate-500 mt-3">Online booking isn't available for this visa. Tap <span className="font-semibold text-[#EA5019]">Get Quote</span> and our team will confirm the final amount and process it for you.</p>
                </div>
              ) : (<>
              <div className="space-y-2 text-sm border-b border-slate-100 pb-4">
                <div className="flex justify-between"><span className="text-slate-600">Embassy (Adult)</span><span className="text-[#0B2545] font-medium">{fmt(visa.fee)} x {counts.adult}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Embassy (Child)</span><span className="text-[#0B2545] font-medium">{fmt(visa.fee)} x {counts.child}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Embassy (Infant)</span><span className="text-[#0B2545] font-medium">{fmt(visa.fee)} x {counts.infant}</span></div>
              </div>
              <div className="space-y-2 text-sm pt-4">
                <div className="flex justify-between"><span className="text-slate-500">Cost</span><span className="text-[#0B2545] font-semibold" data-testid="visa-cost">{fmt(cost)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GST (18%)</span><span className="text-[#0B2545] font-semibold" data-testid="visa-tax">{fmt(tax)}</span></div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 mt-2">
                  <span className="font-heading font-bold text-[#0B2545]">Total Cost</span>
                  <span className="font-heading text-xl font-extrabold text-[#EA5019]" data-testid="visa-total">{fmt(total)}</span>
                </div>
              </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
